import { DropDownOption } from '@/types';
import { Reactive } from 'vue';

// Common shape of `Address` and `App.Data.AddressData` these formatters need.
export type AddressLike = {
    recipient_name?: string | null;
    address_lines: string;
    city: string;
    state: { abbreviation: string };
    zip_code: string;
};

export function capitalize(val: string) {
    return val[0].toUpperCase() + val.substring(1);
}

export function formatMultiLine(text?: string) {
    if (!text) return '';
    return text
        .split('\n')
        .map((l) => `<div class="whitespace-nowrap">${l}</div>`)
        .join('\n');
}

export function addressToPlaintext(
    address?: AddressLike | null,
    appendToLines?: string[],
    noName = false,
) {
    if (!address) return null;
    const lines: string[] = [];
    if (!noName && address.recipient_name) lines.push(address.recipient_name);
    lines.push(...address.address_lines.split('\n'));
    let lastLine = '';
    if (address.city && address.state.abbreviation !== '-') {
        lastLine += `${address.city}, ${address.state.abbreviation}`;
    } else if (address.city) {
        lastLine += address.city;
    } else if (address.state.abbreviation !== '-') {
        lastLine += address.state.abbreviation;
    }
    if (address.zip_code) {
        lastLine += ` ${address.zip_code}`;
    }
    lines.push(lastLine);
    if (appendToLines) {
        for (let i = 0; i < appendToLines.length; i++) {
            if (lines[i]) {
                lines[i] += appendToLines[i];
            }
        }
    }
    // TODO: (probably not good: they might want empty lines in the address)
    return lines
        .filter(
            (l) =>
                l &&
                l !== '' &&
                l !== ' ' &&
                l.trim() !== '\n' &&
                l.trim() !== ',',
        )
        .join('\n');
}

export function addressToHtml(address: AddressLike) {
    const lines = [];

    if (address.recipient_name) lines.push(address.recipient_name);
    lines.push(
        ...address.address_lines.split('\n'),
        `${address.city}, ${address.state.abbreviation} ${address.zip_code}`, // TODO: :/
    );
    return lines.map((l) => `<div>${l}</div>`).join('\n');
}

export function limitLinesReactive<
    T extends object,
    K extends keyof Reactive<T>,
>(maxLines: number, obj: Reactive<T> | null, propName: K) {
    if (!obj) return;

    const val = obj[propName];

    if (typeof val !== 'string') return;
    const lines = val.split('\n');
    if (lines.length > maxLines) {
        obj[propName] = lines.slice(0, maxLines).join('\n') as Reactive<T>[K];
    }
}

const usFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export function formatNumberToAmerican(val: number) {
    return usFormat.format(val);
}

const percentFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});

export function formatPercent(val: number) {
    return percentFormat.format(val);
}

// Display string rebuilt from the structured pack fields, mirroring the backend
// `case_pack` accessor (CasePackParser::rebuild)
export function formatCasePack(
    caseCount?: number | string | null,
    boxCount?: number | string | null,
    eachUnit?: string | null,
) {
    const cases = Number(caseCount) || 0;
    const boxes = Number(boxCount) || 0;
    const each = eachUnit ?? '';
    if (boxes > 0) return `${cases}/${boxes}/${each}`;
    if (cases > 1) return `${cases}/${each}`;
    return eachUnit ?? '';
}

export const ORDER_PRINCIPAL_EMAIL_BODY = `
<p>Aloha,</p>
<p></p>
<p>Thank you for your help in processing the attached new sales order.</p>
<p>Please confirm receipt by return e-mail and potential adjustments at your earliest convenience.</p>
<p></p>
<p>Best Regards,</p>
<p></p>
<p>W H I T B Y  C O M P A N Y<br>
Sales Professionals since 1954<br>
`;
export const ORDER_CUSTOMER_EMAIL_BODY = `
<p>Aloha,</p>
<p></p>
<p>Thank you for your order.</p>
<p>Attached, please find your order confirmation. Should you note any discrepancies, please notify us at once.</p>
<p></p>
<p>Best Regards,</p>
<p></p>
<p>W H I T B Y  C O M P A N Y<br>
Sales Professionals since 1954<br>
`;

export const CLAIM_PRINCIPAL_EMAIL_BODY = `
<p>Aloha,</p>
<p></p>
<p>Thank you for processing the attached claim per details reflected on this document.</p>
<p>We appreciate confirmation of receipt and execution of processing.</p>
<p></p>
<p>Best Regards,</p>
<p></p>
<p>W H I T B Y  C O M P A N Y<br>
Sales Professionals since 1954<br>
`;

export const CLAIM_CUSTOMER_EMAIL_BODY = `
<p>Aloha,</p>
<p></p>
<p>Please find enclosed our deduction document for use on your next remittance to the respective manufacturer.</p>
<p>Thank you!</p>
<p></p>
<p>Best Regards,</p>
<p></p>
<p>W H I T B Y  C O M P A N Y<br>
Sales Professionals since 1954<br>
`;

export const claimCodeOptions: DropDownOption[] = [
    {
        label: '01        CO-OP Advertising Charge - Coupon Book',
        value: '01',
        displayName: '01',
    },
    {
        label: '02        Reimbursement of Scan Allowance',
        value: '02',
        displayName: '02',
    },
    {
        label: '03        Acceleration Allowance',
        value: '03',
        displayName: '03',
    },
    {
        label: '04        Retail Promotional Allowance',
        value: '04',
        displayName: '04',
    },
    {
        label: '05        Promotional Claim',
        value: '05',
        displayName: '05',
    },
    {
        label: '06        Retailer Rebate',
        value: '06',
        displayName: '06',
    },
    {
        label: '07        Retailer Slotting Allowance',
        value: '07',
        displayName: '07',
    },
    {
        label: "08        Broker's Samples",
        value: '08',
        displayName: '08',
    },
    {
        label: '09        Damaged / Unsaleable Merchandise',
        value: '09',
        displayName: '09',
    },
    {
        label: '10        Incorrectly Shipped Items from MFR',
        value: '10',
        displayName: '10',
    },
    {
        label: '11        Short Shipment',
        value: '11',
        displayName: '11',
    },
    {
        label: '12        Price Discrepancy',
        value: '12',
        displayName: '12',
    },
    {
        label: '13        Bill Back Request',
        value: '13',
        displayName: '13',
    },
    {
        label: '14        Reimbursement of Freight Charge',
        value: '14',
        displayName: '14',
    },
    {
        label: '15        Freight Bill',
        value: '15',
        displayName: '15',
    },
    {
        label: '16        Others',
        value: '16',
        displayName: '16',
    },
];

export const claimCategories: DropDownOption[] = [
    { label: '-', value: null },
    {
        label: 'Promotional',
        value: 'Promotional',
    },
    {
        label: 'Advertising',
        value: 'Advertising',
    },
    {
        label: 'Spoils',
        value: 'Spoils',
    },
];

export const orderTypes = [
    {
        name: 'Standard',
        id: 'Standard',
    },
    {
        name: 'Ad-Order',
        id: 'Ad-Order',
    },
    {
        name: 'Halloween',
        id: 'Halloween',
    },
    {
        name: 'Christmas',
        id: 'Christmas',
    },
    {
        name: 'Easter',
        id: 'Easter',
    },
    {
        name: 'Valentine',
        id: 'Valentine',
    },
] as const;

export const shipDatePresets = [
    {
        value: '\nASAP!',
        label: 'ASAP',
    },
    {
        value: 'Deliver to above named carrier by\n#SHIP_DATE\nfor vessel sailing #SAILING_DATE',
        label: 'Deliver to above shipping carrier',
    },
    {
        value: 'Arrival Date:\n#SHIP_DATE',
        label: 'Arrival Date:',
    },
    {
        value: 'Please ship to arrive\nThe week of #SHIP_DATE',
        label: 'Please ship to arrive',
    },
    {
        value: 'Please ship on YB barge sailing from Hilo:\n#SHIP_DATE',
        label: 'Please ship on YB barge sailing',
    },
];
